import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const emptyUserForm = {
  username: "",
  password: "",
  role: "user",
  name: "",
  circle: "",
  rank: "",
  activeStatus: true,
  canEdit: true,
  canDelete: true,
};

const rankOptions = ["", "Assistant Director", "Inspector", "Sub-Inspector"];

const AdminPanel = () => {
  const [tab, setTab] = useState(0);
  const [dashboard, setDashboard] = useState({
    totalUsers: 0,
    deletedUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [archiveRecords, setArchiveRecords] = useState([]);
  const [loading, setLoading] = useState({
    dashboard: false,
    users: false,
    sessions: false,
    archive: false,
  });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userDialogMode, setUserDialogMode] = useState("create");
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [editingUserId, setEditingUserId] = useState(null);
  const [archiveFilter, setArchiveFilter] = useState({
    userId: "",
    from: "",
    to: "",
  });

  const loadDashboard = async () => {
    setLoading((prev) => ({ ...prev, dashboard: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard`);
      if (!res.ok) throw new Error("Failed to load dashboard");
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, dashboard: false }));
    }
  };

  const loadUsers = async () => {
    setLoading((prev) => ({ ...prev, users: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`);
      if (!res.ok) throw new Error("Failed to load users");
      const data = await res.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const loadSessions = async () => {
    setLoading((prev) => ({ ...prev, sessions: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/user-sessions`);
      if (!res.ok) throw new Error("Failed to load sessions");
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, sessions: false }));
    }
  };

  const loadArchive = async (filters = archiveFilter) => {
    setLoading((prev) => ({ ...prev, archive: true }));
    try {
      const params = new URLSearchParams();
      if (filters.userId) params.set("userId", filters.userId);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      const res = await fetch(`${API_BASE}/api/admin/archive?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load archive");
      const data = await res.json();
      setArchiveRecords(data.records || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, archive: false }));
    }
  };

  useEffect(() => {
    loadDashboard();
    loadUsers();
    loadSessions();
    loadArchive();
  }, []);

  const handleUserDialogOpen = (mode, user = null) => {
    setUserDialogMode(mode);
    if (mode === "edit" && user) {
      setEditingUserId(user.id);
      setUserForm({
        username: user.username,
        password: "",
        role: user.role || "user",
        name: user.name || "",
        circle: user.circle || "",
        rank: user.rank || "",
        activeStatus: !!user.activeStatus,
        canEdit: !!user.canEdit,
        canDelete: !!user.canDelete,
      });
    } else {
      setEditingUserId(null);
      setUserForm(emptyUserForm);
    }
    setUserDialogOpen(true);
  };

  const handleUserSave = async () => {
    const payload = {
      username: userForm.username,
      password: userForm.password || undefined,
      role: userForm.role,
      name: userForm.name,
      circle: userForm.circle,
      rank: userForm.rank || null,
      activeStatus: userForm.activeStatus,
      canEdit: userForm.canEdit,
      canDelete: userForm.canDelete,
    };
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users${userDialogMode === "edit" ? `/${editingUserId}` : ""}`,
        {
          method: userDialogMode === "edit" ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to save user");
      await loadUsers();
      setUserDialogOpen(false);
    } catch (error) {
      console.error(error);
      alert("Failed to save user.");
    }
  };

  const handleTogglePermissions = async (user, field) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${user.id}/toggle-permissions`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          canEdit: field === "canEdit" ? !user.canEdit : user.canEdit,
          canDelete: field === "canDelete" ? !user.canDelete : user.canDelete,
        }),
      });
      if (!res.ok) throw new Error("Failed to update permissions");
      await loadUsers();
    } catch (error) {
      console.error(error);
      alert("Failed to update permissions.");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activeStatus: !user.activeStatus }),
      });
      if (!res.ok) throw new Error("Failed to update user");
      await loadUsers();
      await loadDashboard();
    } catch (error) {
      console.error(error);
      alert("Failed to update user.");
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Archive user ${user.username}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${user.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete user");
      await loadUsers();
      await loadDashboard();
    } catch (error) {
      console.error(error);
      alert("Failed to delete user.");
    }
  };

  const handleRestoreArchive = async (record) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/archive/restore/${record.id}`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to restore");
      await loadArchive();
    } catch (error) {
      console.error(error);
      alert("Failed to restore record.");
    }
  };

  const handleDeleteArchive = async (record) => {
    if (!window.confirm("Permanently delete this archive record?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/archive/${record.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete archive");
      await loadArchive();
      await loadDashboard();
    } catch (error) {
      console.error(error);
      alert("Failed to delete archive record.");
    }
  };

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        label: `${user.username} (${user.id})`,
        value: user.id,
      })),
    [users]
  );

  return (
    <Stack spacing={3}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        <Tab label="Dashboard" />
        <Tab label="Users" />
        <Tab label="Archive" />
        <Tab label="Sessions" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          <Typography variant="h6">Admin Dashboard</Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {[
              { label: "Total Users", value: dashboard.totalUsers },
              { label: "Deleted Users", value: dashboard.deletedUsers },
              { label: "Active Users", value: dashboard.activeUsers },
              { label: "Inactive Users", value: dashboard.inactiveUsers },
            ].map((item) => (
              <Card key={item.label} sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="h4">{item.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
          {loading.dashboard && (
            <Typography variant="body2" color="text.secondary">
              Loading dashboard...
            </Typography>
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">User Management</Typography>
            <Button variant="contained" onClick={() => handleUserDialogOpen("create")}>
              Create User
            </Button>
          </Stack>
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Rank</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Can Edit</TableCell>
                  <TableCell>Can Delete</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{user.username}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user.id}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.rank || "—"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={!!user.activeStatus}
                        onChange={() => handleToggleActive(user)}
                        disabled={user.isDeleted}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!user.canEdit}
                        onChange={() => handleTogglePermissions(user, "canEdit")}
                        disabled={user.isDeleted}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!user.canDelete}
                        onChange={() => handleTogglePermissions(user, "canDelete")}
                        disabled={user.isDeleted}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleUserDialogOpen("edit", user)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          onClick={() => handleDeleteUser(user)}
                          disabled={user.isDeleted}
                        >
                          Archive
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {loading.users && (
            <Typography variant="body2" color="text.secondary">
              Loading users...
            </Typography>
          )}
        </Stack>
      )}

      {tab === 2 && (
        <Stack spacing={2}>
          <Typography variant="h6">Archive Records</Typography>
          <Card>
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>User</InputLabel>
                  <Select
                    label="User"
                    value={archiveFilter.userId}
                    onChange={(e) =>
                      setArchiveFilter((prev) => ({ ...prev, userId: e.target.value }))
                    }
                  >
                    <MenuItem value="">All users</MenuItem>
                    {userOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="From"
                  type="date"
                  value={archiveFilter.from}
                  onChange={(e) =>
                    setArchiveFilter((prev) => ({ ...prev, from: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="To"
                  type="date"
                  value={archiveFilter.to}
                  onChange={(e) =>
                    setArchiveFilter((prev) => ({ ...prev, to: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Button variant="contained" onClick={() => loadArchive()}>
                  Apply
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Table</TableCell>
                  <TableCell>Record ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {archiveRecords.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.original_table}</TableCell>
                    <TableCell>{record.original_record_id}</TableCell>
                    <TableCell>{record.username || record.user_id}</TableCell>
                    <TableCell>{record.action_type}</TableCell>
                    <TableCell>{new Date(record.action_timestamp).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleRestoreArchive(record)}>
                          Undo
                        </Button>
                        <Button size="small" color="secondary" onClick={() => handleDeleteArchive(record)}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {loading.archive && (
            <Typography variant="body2" color="text.secondary">
              Loading archive records...
            </Typography>
          )}
        </Stack>
      )}

      {tab === 3 && (
        <Stack spacing={2}>
          <Typography variant="h6">User Sessions</Typography>
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Login</TableCell>
                  <TableCell>Logout</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>System</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.id} hover>
                    <TableCell>{session.id}</TableCell>
                    <TableCell>{session.username}</TableCell>
                    <TableCell>{session.role}</TableCell>
                    <TableCell>{new Date(session.login_time).toLocaleString()}</TableCell>
                    <TableCell>
                      {session.logout_time ? new Date(session.logout_time).toLocaleString() : "Active"}
                    </TableCell>
                    <TableCell>{session.ip_address || "—"}</TableCell>
                    <TableCell>{session.system_info || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {loading.sessions && (
            <Typography variant="body2" color="text.secondary">
              Loading session logs...
            </Typography>
          )}
        </Stack>
      )}

      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{userDialogMode === "edit" ? "Edit User" : "Create User"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={userForm.username}
              onChange={(e) => setUserForm((prev) => ({ ...prev, username: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={userDialogMode === "edit" ? "New Password (optional)" : "Password"}
              type="password"
              value={userForm.password}
              onChange={(e) => setUserForm((prev) => ({ ...prev, password: e.target.value }))}
              required={userDialogMode === "create"}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={userForm.role}
                onChange={(e) => setUserForm((prev) => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Name"
              value={userForm.name}
              onChange={(e) => setUserForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Circle"
              value={userForm.circle}
              onChange={(e) => setUserForm((prev) => ({ ...prev, circle: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Rank</InputLabel>
              <Select
                label="Rank"
                value={userForm.rank}
                onChange={(e) => setUserForm((prev) => ({ ...prev, rank: e.target.value }))}
              >
                {rankOptions.map((rank) => (
                  <MenuItem key={rank} value={rank}>
                    {rank || "None"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider />
            <Stack direction="row" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={userForm.activeStatus}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, activeStatus: e.target.checked }))}
                />
                <Typography variant="body2">Active</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={userForm.canEdit}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, canEdit: e.target.checked }))}
                />
                <Typography variant="body2">Can Edit</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={userForm.canDelete}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, canDelete: e.target.checked }))}
                />
                <Typography variant="body2">Can Delete</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUserSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AdminPanel;
